import {
  levelOf,
  normalizeAccess,
  type AccessLevel,
  type AccessList,
  type AccessResource,
  type AccessUser,
  type ResourcePerm,
  type TeamListFilter,
} from "./types";

const RANK: Record<AccessLevel, number> = { none: 0, view: 1, edit: 2 };

export const broadest = (a: AccessLevel, b: AccessLevel): AccessLevel =>
  RANK[a] >= RANK[b] ? a : b;

// null/absent/"" = Personal.
export const teamOf = (resource: AccessResource) => resource.team || null;

export const ownsResource = (
  user: AccessUser | null,
  resource: AccessResource,
) => !!user && resource.owner === user.id;

export const createdBy = (resource: AccessResource) =>
  resource.created_by || resource.owner;

// An active member of the item's team. Personal items have no members.
export const isActiveMember = (
  user: AccessUser | null,
  resource: AccessResource,
) => {
  const team = teamOf(resource);
  return !!user && !!team && user.teams.includes(team);
};

// The item's team has lapsed: read-only for everyone, owner included.
export const isReadOnly = (
  user: AccessUser | null,
  resource: AccessResource,
) => {
  const team = teamOf(resource);
  return !!user && !!team && !!user.readOnlyTeams?.includes(team);
};

function levelFor(
  user: AccessUser,
  access: AccessList | undefined,
): AccessLevel {
  return broadest(
    levelOf(access?.members?.[user.id]),
    levelOf(access?.everyone),
  );
}

// The project an item inherits from. A pointer to a project in another
// workspace (legacy data; filing forbids it) widens nothing.
export const parentProject = (resource: AccessResource) =>
  resource.project && teamOf(resource.project) === teamOf(resource)
    ? resource.project
    : null;

// The broadest of the user's own and the Everyone entry, on the item and on
// its project. The project's owner counts as Can edit on everything inside.
function accessLevel(
  user: AccessUser,
  resource: AccessResource,
): AccessLevel {
  const project = parentProject(resource);
  if (!project) return levelFor(user, resource.access);
  if (project.owner === user.id) return "edit";
  return broadest(
    levelFor(user, resource.access),
    levelFor(user, project.access),
  );
}

export const isPrivate = (access: AccessList | undefined) => {
  const list = normalizeAccess(access);
  return (
    list.everyone === "none" && Object.keys(list.members ?? {}).length === 0
  );
};

export function resourcePerms(
  user: AccessUser | null,
  resource: AccessResource,
): ResourcePerm {
  const perm = entryPerms(user, resource);
  return perm === "read_write" && isReadOnly(user, resource) ? "read" : perm;
}

function entryPerms(
  user: AccessUser | null,
  resource: AccessResource,
): ResourcePerm {
  if (ownsResource(user, resource)) return "read_write"; // row 1
  // Entries count only for active members of the item's team; Personal
  // content is never shared.
  const level =
    user && isActiveMember(user, resource)
      ? accessLevel(user, resource)
      : "none";
  if (level === "edit") return "read_write"; // row 2
  // Row 4 sits below row 2: a public link adds read, it never caps an editor.
  if (level === "view" || resource.publicLink) return "read"; // rows 3, 4
  return "no_access"; // row 5
}

// What a member sees in the team workspace; the public link never counts.
export function listLevel(
  user: AccessUser,
  resource: AccessResource,
): AccessLevel {
  const level = ownsResource(user, resource)
    ? "edit"
    : isActiveMember(user, resource)
      ? accessLevel(user, resource)
      : "none";
  return level === "edit" && isReadOnly(user, resource) ? "view" : level;
}

// The spec's teamList as a predicate over one team's items. An item an admin
// took over keeps created_by = the leaver, so it shows under Shared with me.
export function inTeamList(
  user: AccessUser,
  team: string,
  resource: AccessResource,
  filter: TeamListFilter,
) {
  if (teamOf(resource) !== team) return false;
  if (listLevel(user, resource) === "none") return false;
  if (filter === "created_by_me") return createdBy(resource) === user.id;
  if (filter === "shared_with_me") return createdBy(resource) !== user.id;
  return true;
}

// Add, change or remove named teammates. Personal content is never shared.
export const canManagePeople = (
  user: AccessUser | null,
  resource: AccessResource,
) => !!teamOf(resource) && resourcePerms(user, resource) === "read_write";

// Everyone entry, public link, archive, delete.
export const canManage = (user: AccessUser | null, resource: AccessResource) =>
  ownsResource(user, resource) && !isReadOnly(user, resource);

// Point an item at a project; never a copy.
export const canFile = (
  user: AccessUser | null,
  item: AccessResource,
  project: AccessResource,
) =>
  ownsResource(user, item) &&
  !isReadOnly(user, item) &&
  teamOf(item) === teamOf(project) &&
  resourcePerms(user, project) === "read_write";

// Unfile an item: its owner, or the owner of the same-workspace project it is
// filed in while still an active member of the item's team.
export const canMoveOut = (user: AccessUser | null, item: AccessResource) => {
  if (isReadOnly(user, item)) return false;
  if (ownsResource(user, item)) return true;
  const project = parentProject(item);
  return !!project && ownsResource(user, project) && isActiveMember(user, item);
};
